'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/FormField';
import { useTranslation } from '@/hooks/useTranslation';
import { isLikelyEmail } from '@/lib/validation';

const API_ENDPOINT = '/api/auth/request-password-reset';

export function ForgotPasswordForm() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | undefined>();
  const [success, setSuccess] = useState(false);
  const { t } = useTranslation();

  const validate = (): string | undefined => {
    if (!email.trim()) return t('auth.validation.emailRequired');
    if (!isLikelyEmail(email)) return t('auth.validation.emailInvalid');
    return undefined;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    const validationError = validate();
    setEmailError(validationError);
    if (validationError) return;

    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || t('auth.forgotPasswordPage.failedToSend')
        );
      }

      setSuccess(true);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('auth.forgotPasswordPage.unexpectedError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center">
        <Alert variant="success" className="mb-6 text-left">
          {email
            ? t('auth.forgotPasswordPage.successMessage', { email })
            : t('auth.forgotPasswordPage.successMessageGeneric')}
        </Alert>

        <p className="mb-4 text-sm text-secondary">
          {t('auth.forgotPasswordPage.checkEmailInstructions')}
        </p>

        <p className="text-sm text-secondary">
          {t('auth.forgotPasswordPage.didntReceiveEmail')}
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="mt-1"
          onClick={() => {
            setSuccess(false);
            setError(null);
          }}
        >
          {t('auth.forgotPasswordPage.tryAgainButton')}
        </Button>

        <div className="mt-6">
          <Link href="/auth/signin" className="link text-sm font-medium">
            {t('auth.forgotPasswordPage.backToSignIn')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        <TextField
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          label={t('auth.forgotPasswordPage.emailLabel')}
          placeholder={t('auth.forgotPasswordPage.emailPlaceholder')}
          value={email}
          onChange={setEmail}
          error={emailError}
        />

        {error && <Alert variant="danger">{error}</Alert>}

        <Button
          type="submit"
          variant="accent"
          size="md"
          className="w-full"
          loading={isLoading}
        >
          {isLoading
            ? t('auth.forgotPasswordPage.sending')
            : t('auth.forgotPasswordPage.sendResetLinkButton')}
        </Button>
      </form>

      <div className="relative mt-6">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-primary" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-secondary px-2 text-muted">
            {t('auth.forgotPasswordPage.orDivider')}
          </span>
        </div>
      </div>

      <div className="mt-6 text-center">
        <Link href="/auth/signin" className="link text-sm font-medium">
          {t('auth.forgotPasswordPage.backToSignIn')}
        </Link>
      </div>
    </>
  );
}
